"use client";

import { Command as CommandPrimitive } from "cmdk";
import { SearchIcon } from "lucide-react";
import type * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { cn } from "@/shared/lib/cn";

function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
        className,
      )}
      {...props}
    />
  );
}

/// Command palette in a dialog. `shouldFilter` is false because every consumer
/// mixes locally-ranked static commands with rows a server already matched;
/// cmdk re-scoring the server rows against their visible label alone would
/// drop legitimate hits (an email-prefix match on a person, for example).
function CommandDialog({
  title = "Command menu",
  description = "Search for a page or a person",
  children,
  className,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
}) {
  return (
    <Dialog {...props}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          "overflow-hidden p-0 sm:max-w-xl",
          // Anchored near the top instead of vertically centred (the Dialog
          // default). A centred box splits every height change across both
          // edges, so the input slides under the cursor while you are still
          // typing into it. Anchoring pins the input and lets only the bottom
          // edge move.
          "top-[12vh] translate-y-0",
          className,
        )}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-3xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-text-quaternary"
        >
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div className="flex items-center gap-2.5 border-b border-border px-4" cmdk-input-wrapper="">
      <SearchIcon className="size-4 shrink-0 text-text-tertiary" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          "flex h-12 w-full bg-transparent py-3 text-sm outline-hidden",
          "placeholder:text-text-quaternary disabled:cursor-not-allowed disabled:opacity-50",
          // The global `:focus-visible` rule paints a 3px ring, which is wrong
          // here twice over: this input is full-bleed with no border of its
          // own, so a ring reads as a box inside a box, and it sits flush
          // against the dialog's clipped top edge, so the ring can only ever
          // render as a cut-off rectangle.
          //
          // Safe to drop: the input is auto-focused on open and is the only
          // focusable element in the dialog (rows are cmdk options driven by
          // arrow keys, not tab stops), so focus cannot be anywhere else while
          // it is open, and the caret already marks it.
          "focus-visible:shadow-none",
          className,
        )}
        {...props}
      />
    </div>
  );
}

/// The list animates its own height between result counts. cmdk measures its
/// inner sizer with a ResizeObserver and publishes the result as
/// `--cmdk-list-height`, so easing `height` to that value turns the snap on
/// every keystroke into a glide.
///
/// The vertical padding sits on the sizer, not here, on purpose: cmdk measures
/// the sizer's own offsetHeight, so padding on the list would be outside the
/// published number and (with border-box sizing) leave the box permanently 12px
/// too short, i.e. always scrolling by exactly its own padding.
///
/// `auto` is the fallback for the first frame, before the observer has
/// reported. Growing from `auto` does not animate, which is correct: the open
/// animation already covers that frame.
function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        "max-h-80 scroll-py-1 overflow-x-hidden overflow-y-auto px-1.5",
        "[&_[cmdk-list-sizer]]:py-1.5",
        "h-[var(--cmdk-list-height,auto)] transition-[height] duration-200 ease-out",
        "motion-reduce:transition-none",
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className={cn("px-3 py-8 text-center text-sm text-text-tertiary", className)}
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn("overflow-hidden py-1 text-foreground", className)}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("-mx-1.5 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

function CommandItem({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "relative flex cursor-default select-none items-center gap-2.5 rounded-md px-3 py-2",
        "text-sm text-text-secondary outline-hidden",
        "data-[selected=true]:bg-accent data-[selected=true]:text-foreground",
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function CommandShortcut({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn("ml-auto font-mono text-xs tracking-widest text-text-quaternary", className)}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
